'use client';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DOMPurify from 'dompurify';

import FileUploadAnswer from '../FileQuestion/FileQuestion';
import LikertFaceQuestion from '../LikertFaceQuestion/LikertFaceQuestion';
import TextQuestion from '../TextQuestion/TextQuestion';
const ComplexQuestion = forwardRef(
  (
    {
      questionData,
      onAnswerChange,
      primaryColor = '#1976d2',
      hasError = false,
      getLikertIcon,
      fontFamily,
      browserSupportsSpeechRecognition = false,
      dictatingIndex = null,
      isRecording = false,
      recordingTime = {},
      resetCount = {},
      pausedQuestions = new Set(),
      maxResets = 3,
      onStartDictation = () => {},
      onPauseDictation = () => {},
      onResumeDictation = () => {},
      onStopDictation = () => {},
      canRecord = () => false,
      getMaxRecordingTime = () => 60,
      getRecordingTooltip = () => 'Grabar',
      formatTime = (s) => `${s}s`,
      getTimeColor = () => 'inherit',
      transcript = '',
      baseTextRef = { current: '' },
      hasOwnBold,
    },
    ref,
  ) => {
    const [selectedOptions, setSelectedOptions] = useState([
      {
        id: crypto.randomUUID(),
        optionName: '',
        selectedChildAnswers: {},
      },
    ]);
    const previousCompleteRef = useRef({});
    const [expandedAccordions, setExpandedAccordions] = useState({});
    const localRef = useRef(null); // ✅ Nuevo ref DOM interno
    useImperativeHandle(ref, () => ({
      validate: () => validateAll(),
      getAnswers: () => selectedOptions,
      hasErrors: () => validateAll().length > 0,
      scrollIntoView: () => {
        localRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      },
    }));

    const validateAll = () => {
      const errors = [];
      selectedOptions.forEach((option, index) => {
        if (!option.optionName) {
          errors.push(`Opción ${index + 1}: Selecciona una opción`);
        }
        if (option.optionName && questionData.childrenQuestions.length > 0) {
          questionData.childrenQuestions.forEach((child) => {
            const answer = option.selectedChildAnswers[child.questionId];
            // Para múltiple, verificar que el array tenga al menos un elemento
            const hasAnswer = Array.isArray(answer)
              ? answer.length > 0
              : typeof answer === 'string'
                ? answer.length > 0 // ✅ cubre tanto string simple como "op1, op2"
                : !!answer;
            if (child.required === true && !hasAnswer) {
              errors.push(
                `Opción ${index + 1}: Responde "${child.questionName}"`,
              );
            }
          });
        }
      });
      return errors;
    };

    useEffect(() => {
      if (hasError) {
        const newExpanded = {};
        selectedOptions.forEach((option) => {
          const needsOpen = questionData.childrenQuestions.some(
            (child) => !option.selectedChildAnswers[child.questionId],
          );
          if (needsOpen) newExpanded[option.id] = true;
        });
        setExpandedAccordions((prev) => ({ ...prev, ...newExpanded }));
      }
    }, [hasError, selectedOptions]);

    // Agrega este useEffect dentro del componente:
    useEffect(() => {
      if (dictatingIndex === null || !transcript) return;

      const parts = String(dictatingIndex).split('-');
      if (parts.length < 2) return; // es pregunta normal, no hija

      const childQuestionId = parts[parts.length - 1];
      const optId = parts.slice(0, -1).join('-');

      const base = baseTextRef.current || '';
      const merged =
        base && transcript ? `${base} ${transcript}` : `${base}${transcript}`;

      setSelectedOptions((prev) =>
        prev.map((opt) =>
          opt.id === optId
            ? {
                ...opt,
                selectedChildAnswers: {
                  ...opt.selectedChildAnswers,
                  [childQuestionId]: merged,
                },
              }
            : opt,
        ),
      );
    }, [transcript]);

    const getAvailableOptions = (currentId) => {
      const used = selectedOptions
        .filter((opt) => opt.id !== currentId && opt.optionName)
        .map((opt) => opt.optionName);
      return questionData.options.filter(
        (opt) => !used.includes(opt.optionName),
      );
    };

    const handleOptionSelect = (id, name) => {
      const updated = selectedOptions.map((opt) =>
        opt.id === id
          ? {
              ...opt,
              optionName: name,
              selectedChildAnswers:
                name === opt.optionName ? opt.selectedChildAnswers : {},
            }
          : opt,
      );
      setSelectedOptions(updated);
      onAnswerChange?.(serializeOptions(updated));
      setExpandedAccordions((prev) => ({
        ...prev,
        [id]: true,
      }));
    };

    // ✅ Serializa los archivos (File) a nombres y normaliza arrays antes de
    // mandar las respuestas al padre. El estado interno conserva los File reales;
    // esto evita que sessionStorage (JSON.stringify) convierta los File en {}.
    const serializeOptions = (options) =>
      options.map((opt) => ({
        ...opt,
        selectedChildAnswers: Object.fromEntries(
          Object.entries(opt.selectedChildAnswers).map(([key, value]) => {
            const child = questionData.childrenQuestions.find(
              (c) => c.questionId.toString() === key,
            );
            if (child?.typeQuestion === 'Archivos') {
              const fileNames = Array.isArray(value)
                ? value
                    .map((f) => (f instanceof File ? f.name : f))
                    .filter(Boolean)
                : [];
              return [key, fileNames.join(', ')];
            }
            if (child?.typeQuestion === 'Opción Múltiple') {
              return [key, Array.isArray(value) ? value.join(', ') : value];
            }
            return [key, value];
          }),
        ),
      }));

    const handleChildAnswerChange = (
      optionId,
      questionId,
      answer,
      typeQuestion,
    ) => {
      // ✅ Estado interno SIEMPRE guarda el valor real (File objects incluidos)
      const updated = selectedOptions.map((opt) =>
        opt.id === optionId
          ? {
              ...opt,
              selectedChildAnswers: {
                ...opt.selectedChildAnswers,
                [questionId]: answer, // ✅ File objects reales aquí
              },
            }
          : opt,
      );
      setSelectedOptions(updated);

      // ✅ Para el callback externo, serializar archivos a nombres
      onAnswerChange?.(serializeOptions(updated));
    };

    const addOption = () => {
      if (selectedOptions.length < questionData.options.length) {
        // ✅ Preservamos las opciones existentes (incluidos los File ya cargados)
        // y notificamos al padre con datos serializados.
        const updated = [
          ...selectedOptions,
          {
            id: crypto.randomUUID(),
            optionName: '',
            selectedChildAnswers: {},
          },
        ];
        setSelectedOptions(updated);
        onAnswerChange?.(serializeOptions(updated));
      }
    };

    const removeOption = (id) => {
      if (selectedOptions.length > 1) {
        const updated = selectedOptions.filter((opt) => opt.id !== id);
        setSelectedOptions(updated);
        onAnswerChange?.(serializeOptions(updated));
      }
    };

    const handleAccordionChange = (id) => (e, expanded) => {
      setExpandedAccordions((prev) => ({
        ...prev,
        [id]: expanded,
      }));
    };

    const getErrors = (option) => {
      const selectError = hasError && !option.optionName;
      const childErrors = {};
      if (hasError && option.optionName) {
        questionData.childrenQuestions.forEach((child) => {
          if (
            child.required === true &&
            !option.selectedChildAnswers[child.questionId]
          ) {
            childErrors[child.questionId] = true;
          }
        });
      }
      return { selectError, childErrors };
    };

    useEffect(() => {
      // Revisar cada opción individualmente
      selectedOptions.forEach((option) => {
        if (!option.optionName || questionData.childrenQuestions.length === 0) {
          return;
        }

        const hasTextQuestions = questionData.childrenQuestions.some(
          (child) => child.typeQuestion === 'Texto',
        );
        // ✅ Si hay un hijo de Archivos, NO colapsar: el usuario debe seguir
        // viendo el archivo que cargó (si no, parece que se borró y lo re-sube).
        const hasFileQuestions = questionData.childrenQuestions.some(
          (child) => child.typeQuestion === 'Archivos',
        );
        // Verificar si TODAS las preguntas hijas están completas
        const isComplete = questionData.childrenQuestions.every((child) => {
          const answer = option.selectedChildAnswers[child.questionId];
          // Para texto, verificar que no esté vacío
          if (child.typeQuestion === 'Texto') {
            return answer && answer.trim() !== '';
          }
          if (child.typeQuestion === 'Opción Múltiple') {
            const raw = option.selectedChildAnswers[child.questionId];
            if (typeof raw === 'string') return raw.length > 0;
            return Array.isArray(raw) && raw.length > 0;
          }
          // Para otros tipos, solo verificar que exista
          return !!answer;
        });

        // Solo colapsar si acaba de completarse (transición de incompleto a completo)
        const wasComplete = previousCompleteRef.current[option.id];

        if (isComplete && !wasComplete && !hasTextQuestions && !hasFileQuestions) {
          // Pequeño delay para que el usuario vea que completó todo antes de cerrar
          setTimeout(() => {
            setExpandedAccordions((prev) => ({
              ...prev,
              [option.id]: false,
            }));
          }, 500); // Espera medio segundo
        }

        // Actualizar el estado anterior
        previousCompleteRef.current[option.id] = isComplete;
      });
    }, [selectedOptions, questionData.childrenQuestions]);

    const canAddMore = selectedOptions.length < questionData.options.length;

    // ✅ Agregar esta función después de getErrors
    const shouldBlink = (option) => {
      if (!option.optionName || questionData.childrenQuestions.length === 0) {
        return false;
      }

      // Verificar si tiene preguntas de texto
      const hasTextQuestions = questionData.childrenQuestions.some(
        (child) => child.typeQuestion === 'Texto',
      );

      if (!hasTextQuestions) {
        return false;
      }

      // Verificar si está completo
      const isComplete = questionData.childrenQuestions.every((child) => {
        const answer = option.selectedChildAnswers[child.questionId];

        if (child.typeQuestion === 'Texto') {
          return answer && answer.trim() !== '';
        }
        return !!answer;
      });

      // Parpadear si está completo Y abierto
      return isComplete && expandedAccordions[option.id];
    };
    return (
      <Box ref={ref}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: hasOwnBold(questionData.questionName)
              ? 'normal'
              : 'bold',
            mb: '0.8rem',
            color: hasError ? 'red' : 'rgba(0,0,0,0.6)',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(questionData.questionName),
            }}
          />
        </Typography>
        {questionData.description && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontStyle: 'italic',
              mb: '1rem',
              color: hasError ? 'red' : 'text.secondary',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(questionData.description),
              }}
            />
          </Typography>
        )}

        {selectedOptions.map((opt, index) => {
          const { selectError, childErrors } = getErrors(opt);
          const hasChildErrors = Object.keys(childErrors).length > 0;

          return (
            <Box
              key={opt.id}
              sx={{
                mb: 3,
                p: 2,
                border:
                  selectError || hasChildErrors
                    ? '2px solid #f44336'
                    : '1px solid #ccc',
                borderRadius: 2,
                backgroundColor:
                  selectError || hasChildErrors ? '#ffebee' : 'transparent',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                translate="no"
              >
                <Autocomplete
                  fullWidth
                  options={getAvailableOptions(opt.id)}
                  getOptionLabel={(option) => option.optionName}
                  value={
                    getAvailableOptions(opt.id).find(
                      (o) => o.optionName === opt.optionName,
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    handleOptionSelect(opt.id, newValue?.optionName || '');
                  }}
                  componentsProps={{
                    paper: {
                      sx: {
                        fontFamily,
                        '& .MuiAutocomplete-option': {
                          fontFamily,
                        },
                      },
                    },
                  }}
                  renderOption={(props, option) => (
                    <li {...props} translate="no">
                      {option.optionName}
                    </li>
                  )}
                  renderInput={(params) => (
                    <FormControl fullWidth error={selectError}>
                      <InputLabel shrink>{`Opción ${index + 1}`}</InputLabel>
                      <TextField
                        {...params}
                        variant="outlined"
                        error={selectError}
                        label={`Opción ${index + 1}`}
                        helperText={
                          selectError
                            ? 'Por favor selecciona una opción'
                            : undefined
                        }
                        inputProps={{
                          ...params.inputProps,
                          translate: 'no',
                        }}
                      />
                    </FormControl>
                  )}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {canAddMore && index === selectedOptions.length - 1 && (
                    <IconButton onClick={addOption} color="success">
                      <AddIcon />
                    </IconButton>
                  )}
                  {selectedOptions.length > 1 && (
                    <IconButton
                      onClick={() => removeOption(opt.id)}
                      color="error"
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {opt.optionName && questionData.childrenQuestions.length > 0 && (
                <Accordion
                  sx={{
                    mt: 2,
                    border: hasChildErrors
                      ? '1px solid #f44336'
                      : '1px solid #e0e0e0',
                    '&:before': { display: 'none' },
                  }}
                  expanded={expandedAccordions[opt.id] || false}
                  onChange={handleAccordionChange(opt.id)}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        animation: shouldBlink(opt)
                          ? 'blink 1.5s infinite'
                          : 'none',
                        color: shouldBlink(opt) ? primaryColor : 'inherit',
                        // ✅ Fondo circular que parpadea
                        backgroundColor: shouldBlink(opt)
                          ? `${primaryColor}50`
                          : 'transparent',
                        borderRadius: '50%',
                        padding: '4px',
                        // ✅ REMOVER: transform y transition - dejar que funcione normal
                      },
                      // ✅ REMOVER: la regla &.Mui-expanded
                      '@keyframes blink': {
                        '0%, 100%': {
                          opacity: 1,
                          boxShadow: shouldBlink(opt)
                            ? `0 0 0 0 ${primaryColor}40`
                            : 'none',
                        },
                        '50%': {
                          opacity: 0.6,
                          boxShadow: shouldBlink(opt)
                            ? `0 0 0 8px ${primaryColor}00`
                            : 'none',
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: hasChildErrors ? '#f44336' : 'inherit' }}
                    >
                      Preguntas relacionadas con "{opt.optionName}"
                      {hasChildErrors && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ ml: 1, color: '#f44336' }}
                        >
                          (Faltan respuestas)
                        </Typography>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {questionData.childrenQuestions.map((child) => {
                      const childError = childErrors[child.questionId];
                      const typeQuestion = child.typeQuestion;
                      return (
                        <Box
                          key={child.questionId}
                          sx={{
                            mb: 3,
                            p: 2,
                            border: childError
                              ? '1px solid #f44336'
                              : '1px solid transparent',
                            borderRadius: 1,
                            backgroundColor: childError
                              ? '#ffebee'
                              : 'transparent',
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              color: childError ? '#f44336' : 'inherit',
                              fontWeight: childError ? 'bold' : 'normal',
                            }}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  (child.questionName || '')
                                    .replace(/<p>/g, '')
                                    .replace(/<\/p>/g, ' ')
                                    .replace(/<br\s*\/?>/g, ' ')
                                    .trim(),
                                ),
                              }}
                            />
                            {/*child.questionName*/}
                          </Typography>
                          {child.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    (child.description || '')
                                      .replace(/<p>/g, '')
                                      .replace(/<\/p>/g, ' ')
                                      .replace(/<br\s*\/?>/g, ' ')
                                      .trim(),
                                  ),
                                }}
                              />
                              {/*child.description*/}
                            </Typography>
                          )}

                          {/* Tipo 1: Texto corto */}
                          {typeQuestion === 'Texto' && (
                            <TextQuestion
                              questionId={child.questionId}
                              typeQuestion={child.typeQuestion}
                              questionName=""
                              description=""
                              index={`${opt.id}-${child.questionId}`}
                              unansweredQuestions={
                                childError
                                  ? [`${opt.id}-${child.questionId}`]
                                  : []
                              }
                              formValue={{
                                value:
                                  opt.selectedChildAnswers[child.questionId] ||
                                  '',
                              }}
                              onChange={(e) =>
                                handleChildAnswerChange(
                                  opt.id,
                                  child.questionId,
                                  e.target.value,
                                )
                              }
                              settings={{
                                textType: child.settings?.textType || 'text',
                                haveLimit: child.settings?.haveLimit || false,
                                wordSizeLimit:
                                  child.settings?.wordSizeLimit || 0,
                                microphoneEnabled:
                                  child.settings?.microphoneEnabled || false,
                              }}
                              sliderErrors={
                                childError
                                  ? {
                                      [`${opt.id}-${child.questionId}`]:
                                        'Este campo es obligatorio',
                                    }
                                  : {}
                              }
                              onErrorClear={() => {}}
                              primaryColor={primaryColor}
                              browserSupportsSpeechRecognition={
                                browserSupportsSpeechRecognition
                              }
                              dictatingIndex={dictatingIndex}
                              isRecording={isRecording}
                              recordingTime={recordingTime}
                              resetCount={resetCount}
                              pausedQuestions={pausedQuestions}
                              maxResets={maxResets}
                              onStartDictation={(idx) =>
                                onStartDictation(
                                  `${opt.id}-${child.questionId}`,
                                  opt.selectedChildAnswers[child.questionId] ||
                                    '',
                                )
                              }
                              onPauseDictation={onPauseDictation}
                              onResumeDictation={onResumeDictation}
                              onStopDictation={onStopDictation}
                              canRecord={canRecord}
                              getMaxRecordingTime={getMaxRecordingTime}
                              getRecordingTooltip={getRecordingTooltip}
                              formatTime={formatTime}
                              getTimeColor={getTimeColor}
                              showDivider={false}
                              hasOwnBold={hasOwnBold}
                            />
                          )}

                          {/* Tipo 8: Opción única (Radio) */}
                          {(typeQuestion === 'Selección' ||
                            typeQuestion === 'Escala Likert') && (
                            <>
                              <RadioGroup
                                sx={{ mt: 1 }}
                                value={
                                  opt.selectedChildAnswers[child.questionId] ||
                                  ''
                                }
                                onChange={(e) =>
                                  handleChildAnswerChange(
                                    opt.id,
                                    child.questionId,
                                    e.target.value,
                                  )
                                }
                              >
                                {child.options.map((copt) => (
                                  <FormControlLabel
                                    key={copt.numberOption}
                                    value={copt.optionName}
                                    control={
                                      <Radio
                                        sx={{
                                          color: childError
                                            ? '#f44336'
                                            : primaryColor,
                                          '&.Mui-checked': {
                                            color: primaryColor,
                                          },
                                        }}
                                      />
                                    }
                                    label={copt.optionName}
                                  />
                                ))}
                              </RadioGroup>
                              {childError && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#f44336',
                                    mt: 1,
                                    display: 'block',
                                  }}
                                >
                                  Por favor selecciona una respuesta
                                </Typography>
                              )}
                            </>
                          )}

                          {/* Tipo 22: Escala de Opinión */}

                          {typeQuestion === 'Escala de Opinión' && (
                            <>
                              {/* Etiquetas en móvil */}
                              <Box
                                display={{ xs: 'flex', md: 'none' }}
                                flexDirection="column"
                                width="100%"
                                maxWidth="600px"
                                mt={1}
                                px={2}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                  }}
                                >
                                  {child.textBipolarBar?.valueLeft || 0}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: '1rem',
                                      color: primaryColor,
                                    }}
                                  >
                                    ➔
                                  </Box>
                                  {child.textBipolarBar?.leftText || 'Mínimo'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                  }}
                                >
                                  {child.textBipolarBar?.valueRight || 10}
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: '1rem',
                                      color: primaryColor,
                                    }}
                                  >
                                    ➔
                                  </Box>
                                  {child.textBipolarBar?.rightText || 'Máximo'}
                                </Typography>
                              </Box>

                              {/* Botones de escala */}
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                mt={2}
                                width="100%"
                              >
                                <ToggleButtonGroup
                                  value={
                                    opt.selectedChildAnswers[child.questionId]
                                      ? Number(
                                          opt.selectedChildAnswers[
                                            child.questionId
                                          ],
                                        )
                                      : null
                                  }
                                  exclusive
                                  aria-label="Escala de opinión"
                                  onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                      handleChildAnswerChange(
                                        opt.id,
                                        child.questionId,
                                        newValue.toString(),
                                      );
                                    }
                                  }}
                                  sx={{
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    width: '100%',
                                  }}
                                >
                                  {Array.from(
                                    {
                                      length:
                                        Number(
                                          child.textBipolarBar?.valueRight ||
                                            10,
                                        ) -
                                        Number(
                                          child.textBipolarBar?.valueLeft || 0,
                                        ) +
                                        1,
                                    },
                                    (_, i) =>
                                      Number(
                                        child.textBipolarBar?.valueLeft || 0,
                                      ) + i,
                                  ).map((num) => (
                                    <ToggleButton
                                      key={num}
                                      value={num}
                                      sx={{
                                        width: { xs: 36, sm: 42, md: 48 },
                                        height: { xs: 36, sm: 42, md: 48 },
                                        m: 0.5,
                                        borderRadius: 2,
                                        border: childError
                                          ? '1px solid #f44336'
                                          : undefined,
                                        '&.Mui-selected': {
                                          backgroundColor:
                                            primaryColor || '#1976d2',
                                          color: '#fff',
                                        },
                                        '&.Mui-selected:hover': {
                                          backgroundColor:
                                            primaryColor || '#1565c0',
                                        },
                                      }}
                                    >
                                      {num}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>

                                {/* Etiquetas en escritorio */}
                                <Box
                                  display={{ xs: 'none', md: 'flex' }}
                                  justifyContent="space-between"
                                  width="100%"
                                  maxWidth="600px"
                                  mt={1}
                                  px={2}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {child.textBipolarBar?.leftText || 'Mínimo'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      textAlign: 'right',
                                    }}
                                  >
                                    {child.textBipolarBar?.rightText ||
                                      'Máximo'}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Mensaje de error */}
                              {childError && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#f44336',
                                    mt: 1,
                                    display: 'block',
                                  }}
                                >
                                  Por favor selecciona un valor
                                </Typography>
                              )}
                            </>
                          )}

                          {/* Tipo 3: Opción múltiple (Checkbox) */}
                          {typeQuestion === 'Opción Múltiple' && (
                            <FormGroup sx={{ mt: 1 }}>
                              {child.options.map((copt) => {
                                const raw =
                                  opt.selectedChildAnswers[child.questionId] ||
                                  '';
                                const currentAnswers =
                                  typeof raw === 'string' && raw.length > 0
                                    ? raw.split(', ')
                                    : Array.isArray(raw)
                                      ? raw
                                      : [];
                                const isChecked = currentAnswers.includes(
                                  copt.optionName,
                                );
                                const hasLimit = child.score && child.score > 0;
                                const isDisabled =
                                  hasLimit &&
                                  !isChecked &&
                                  currentAnswers.length >= child.score;

                                return (
                                  <FormControlLabel
                                    key={copt.numberOption}
                                    control={
                                      <Checkbox
                                        checked={isChecked}
                                        name={copt.optionName}
                                        value={copt.optionName}
                                        disabled={isDisabled}
                                        onChange={(e) => {
                                          const newAnswers = e.target.checked
                                            ? [
                                                ...currentAnswers,
                                                copt.optionName,
                                              ]
                                            : currentAnswers.filter(
                                                (a) => a !== copt.optionName,
                                              );
                                          handleChildAnswerChange(
                                            opt.id,
                                            child.questionId,
                                            newAnswers,
                                            'Opción Múltiple',
                                          );
                                        }}
                                        sx={{
                                          color: childError
                                            ? '#f44336'
                                            : primaryColor,
                                          '&.Mui-checked': {
                                            color: primaryColor,
                                          },
                                        }}
                                      />
                                    }
                                    label={copt.optionName}
                                  />
                                );
                              })}

                              {/* Indicador de límite */}
                              {child.score > 0 &&
                                (() => {
                                  const raw =
                                    opt.selectedChildAnswers[
                                      child.questionId
                                    ] || '';
                                  const count =
                                    typeof raw === 'string' && raw.length > 0
                                      ? raw.split(', ').length
                                      : Array.isArray(raw)
                                        ? raw.length
                                        : 0;
                                  return (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mt: 0.5,
                                        color:
                                          count >= child.score
                                            ? 'error.main'
                                            : 'rgba(0,0,0,0.6)',
                                        fontWeight:
                                          count >= child.score
                                            ? 'bold'
                                            : 'normal',
                                      }}
                                    >
                                      {count} / {child.score} seleccionadas
                                    </Typography>
                                  );
                                })()}

                              {childError && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#f44336',
                                    mt: 1,
                                    display: 'block',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Debes seleccionar al menos una opción
                                </Typography>
                              )}
                            </FormGroup>
                          )}

                          {typeQuestion === 'Archivos' && (
                            <FileUploadAnswer
                              key={`${opt.id}-${child.questionId}`}
                              instanceId={`${opt.id}-${child.questionId}`}
                              question={{
                                questionId: child.questionId,
                                questionName: '',
                                description: '',
                                required: child.required,
                                options: child.options,
                                score: child.score,
                                maxFiles: child.maxFiles,
                              }}
                              answer={
                                opt.selectedChildAnswers[child.questionId] || []
                              }
                              setAnswer={(files) =>
                                handleChildAnswerChange(
                                  opt.id,
                                  child.questionId,
                                  files,
                                  'Archivos',
                                )
                              }
                              showUploadError={childError}
                              hasOwnBold={hasOwnBold}
                            />
                          )}
                          {typeQuestion === 'Sentimental' && (
                            <LikertFaceQuestion
                              questionName={''}
                              description={''}
                              options={child.options}
                              currentValue={
                                opt.selectedChildAnswers[child.questionId] || ''
                              }
                              questionId={child.questionId}
                              typeQuestion={child.typeQuestion}
                              index={index}
                              hasError={childError}
                              primaryColor={primaryColor}
                              getLikertIcon={getLikertIcon}
                              onValueChange={(value) =>
                                handleChildAnswerChange(
                                  opt.id,
                                  child.questionId,
                                  value,
                                )
                              }
                              hasOwnBold={hasOwnBold}
                            />
                          )}

                          {/* Agregar más tipos según necesites */}
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          );
        })}

        {!canAddMore && (
          <Typography variant="body2" color="text.secondary" align="center">
            Has seleccionado todas las opciones disponibles.
          </Typography>
        )}

        <Divider variant="middle" sx={{ marginTop: '10px' }} />
      </Box>
    );
  },
);

ComplexQuestion.displayName = 'ComplexQuestion';

export default ComplexQuestion;
